import { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { api, formatApiError } from "../lib/api";
import { Calendar, Save, ArrowLeft, AlertCircle, ListChecks, Pencil, Brain, Sparkles, Upload } from "lucide-react";

const initialContest = {
    title: "", slug: "", description: "", cover_image: "",
    organizer: "Cục Dân số - Bộ Y tế",
    start_at: "", end_at: "",
    duration_minutes: 30, num_mcq: 10,
    has_essay: true, essay_prompt: "", essay_rubric: "",
    voting_enabled: true,
    ai_provider: "minimax",
    status: "draft",
};

export default function AdminContestForm() {
    const { id } = useParams();
    const navigate = useNavigate();
    const isEdit = Boolean(id);
    const [form, setForm] = useState(initialContest);
    const [loading, setLoading] = useState(isEdit);
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState("");
    const [success, setSuccess] = useState("");
    const [aiBusy, setAiBusy] = useState(false);
    const [uploadingCover, setUploadingCover] = useState(false);

    const aiGenerate = async () => {
        const topic = form.title?.trim();
        if (!topic) { setError("Hãy nhập tiêu đề trước khi dùng AI tạo mô tả."); return; }
        setError(""); setAiBusy(true);
        try {
            const { data } = await api.post("/admin/ai/generate-description", { topic });
            setForm((f) => ({ ...f, description: data.description }));
            setSuccess(`Đã tạo mô tả bằng ${data.provider_used}.`);
        } catch (e) {
            setError(formatApiError(e.response?.data?.detail));
        } finally { setAiBusy(false); }
    };

    const handleCoverUpload = async (e) => {
        const f = e.target.files?.[0];
        if (!f) return;
        setError(""); setUploadingCover(true);
        try {
            const fd = new FormData();
            fd.append("file", f);
            const { data } = await api.post("/admin/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
            const API = process.env.REACT_APP_BACKEND_URL;
            setForm((f2) => ({ ...f2, cover_image: API + data.url }));
            setSuccess("Đã tải lên ảnh bìa.");
        } catch (e2) {
            setError(formatApiError(e2.response?.data?.detail));
        } finally { setUploadingCover(false); }
    };

    useEffect(() => {
        if (isEdit) {
            api.get(`/contests/${id}`).then((r) => {
                const c = r.data;
                setForm({
                    ...c,
                    start_at: c.start_at?.slice(0, 16) || "",
                    end_at: c.end_at?.slice(0, 16) || "",
                });
                setLoading(false);
            }).catch(() => { setError("Không tải được dữ liệu"); setLoading(false); });
        }
    }, [id, isEdit]);

    const slugify = (s) =>
        s.toLowerCase()
            .normalize("NFD").replace(/[\u0300-\u036f]/g, "")
            .replace(/đ/g, "d")
            .replace(/[^a-z0-9]+/g, "-")
            .replace(/^-|-$/g, "");

    const update = (k, v) => setForm((f) => ({ ...f, [k]: v }));

    const save = async () => {
        setError(""); setSuccess("");
        if (!form.title || !form.description || !form.start_at || !form.end_at) {
            setError("Vui lòng điền đủ Tiêu đề, Mô tả, Thời gian bắt đầu/kết thúc.");
            return;
        }
        setSaving(true);
        try {
            const payload = {
                ...form,
                slug: form.slug || slugify(form.title),
                duration_minutes: Number(form.duration_minutes),
                num_mcq: Number(form.num_mcq),
                start_at: new Date(form.start_at).toISOString(),
                end_at: new Date(form.end_at).toISOString(),
            };
            if (isEdit) {
                await api.put(`/admin/contests/${id}`, payload);
                setSuccess("Đã lưu cuộc thi.");
            } else {
                const { data } = await api.post("/admin/contests", payload);
                setSuccess("Đã tạo cuộc thi mới.");
                setTimeout(() => navigate(`/quan-tri/cuoc-thi/${data.id}/cau-hoi`), 700);
            }
        } catch (e) {
            setError(formatApiError(e.response?.data?.detail));
        } finally {
            setSaving(false);
        }
    };

    if (loading) return <div className="p-6 lg:p-10"><div className="card h-64 shimmer" /></div>;

    return (
        <div className="p-6 lg:p-10 max-w-5xl">
            <Link to="/quan-tri" className="btn-ghost mb-4" data-testid="back-admin"><ArrowLeft className="w-4 h-4" /> Về Quản trị</Link>
            <h1 className="font-heading text-3xl font-bold mb-2">{isEdit ? "Chỉnh sửa cuộc thi" : "Tạo cuộc thi mới"}</h1>
            <p className="text-slate-600 text-sm mb-6">Cấu hình thông tin, thời gian, AI provider và đề tự luận.</p>

                <div className="card p-7 space-y-5">
                    <Section title="Thông tin chung" icon={<Pencil className="w-4 h-4" />}>
                        <Field label="Tiêu đề" required>
                            <input className="input" value={form.title} onChange={(e) => update("title", e.target.value)}
                                onBlur={() => !form.slug && update("slug", slugify(form.title))} data-testid="title-input" />
                        </Field>
                        <Field label="Slug (URL)">
                            <input className="input font-mono" value={form.slug} onChange={(e) => update("slug", e.target.value)} placeholder="tu-dong-tao-tu-tieu-de" data-testid="slug-input" />
                        </Field>
                        <Field label="Mô tả" required>
                            <textarea className="input" rows={4} value={form.description} onChange={(e) => update("description", e.target.value)} data-testid="desc-input" />
                            <div className="flex justify-end mt-2">
                                <button type="button" onClick={aiGenerate} disabled={aiBusy} className="btn-secondary !py-1.5 !px-3 text-xs" data-testid="ai-gen-desc-btn">
                                    {aiBusy ? <><span className="inline-block w-3 h-3 rounded-full border-2 border-[var(--gold-deep)] border-t-transparent animate-spin" /> Đang tạo…</>
                                        : <><Sparkles className="w-3.5 h-3.5" /> AI tạo mô tả từ tiêu đề</>}
                                </button>
                            </div>
                        </Field>
                        <Field label="Đơn vị tổ chức">
                            <input className="input" value={form.organizer} onChange={(e) => update("organizer", e.target.value)} data-testid="organizer-input" />
                        </Field>
                        <Field label="Ảnh bìa">
                            <div className="flex gap-2 items-start">
                                <input className="input flex-1" value={form.cover_image || ""} onChange={(e) => update("cover_image", e.target.value)} placeholder="URL hoặc tải lên file" data-testid="cover-input" />
                                <label className="btn-secondary !py-2 !px-3 text-xs cursor-pointer">
                                    <Upload className="w-4 h-4" />{uploadingCover ? "Đang tải…" : "Tải ảnh"}
                                    <input type="file" hidden accept="image/*" onChange={handleCoverUpload} data-testid="cover-upload-input" />
                                </label>
                            </div>
                            {form.cover_image && (
                                <img src={form.cover_image} alt="cover preview" className="mt-2 h-32 rounded-lg border border-[var(--border)] object-cover" />
                            )}
                        </Field>
                    </Section>

                    <Section title="Thời gian & cấu trúc" icon={<Calendar className="w-4 h-4" />}>
                        <div className="grid sm:grid-cols-2 gap-4">
                            <Field label="Bắt đầu" required>
                                <input type="datetime-local" className="input" value={form.start_at} onChange={(e) => update("start_at", e.target.value)} data-testid="start-input" />
                            </Field>
                            <Field label="Kết thúc" required>
                                <input type="datetime-local" className="input" value={form.end_at} onChange={(e) => update("end_at", e.target.value)} data-testid="end-input" />
                            </Field>
                            <Field label="Thời lượng làm bài (phút)">
                                <input type="number" min={5} className="input" value={form.duration_minutes} onChange={(e) => update("duration_minutes", e.target.value)} data-testid="duration-input" />
                            </Field>
                            <Field label="Số câu trắc nghiệm">
                                <input type="number" min={1} className="input" value={form.num_mcq} onChange={(e) => update("num_mcq", e.target.value)} data-testid="num-mcq-input" />
                            </Field>
                        </div>
                    </Section>

                    <Section title="Phần tự luận" icon={<ListChecks className="w-4 h-4" />}>
                        <label className="flex items-center gap-3 cursor-pointer">
                            <input type="checkbox" checked={form.has_essay} onChange={(e) => update("has_essay", e.target.checked)} data-testid="has-essay-cb" />
                            <span className="text-sm font-medium">Có phần thi tự luận</span>
                        </label>
                        {form.has_essay && (
                            <>
                                <Field label="Đề bài tự luận">
                                    <textarea className="input" rows={3} value={form.essay_prompt || ""} onChange={(e) => update("essay_prompt", e.target.value)} data-testid="essay-prompt-input" />
                                </Field>
                                <Field label="Rubric chấm">
                                    <textarea className="input" rows={2} value={form.essay_rubric || ""} onChange={(e) => update("essay_rubric", e.target.value)} placeholder="Tiêu chí 1: …  Tiêu chí 2: …" data-testid="essay-rubric-input" />
                                </Field>
                            </>
                        )}
                    </Section>

                    <Section title="AI & Cộng đồng" icon={<Brain className="w-4 h-4" />}>
                        <Field label="AI provider chấm tự luận">
                            <select className="input" value={form.ai_provider} onChange={(e) => update("ai_provider", e.target.value)} data-testid="ai-provider-select">
                                <option value="minimax">Minimax 2.7 (mặc định)</option>
                                <option value="openrouter_gpt5">OpenRouter · GPT-5</option>
                                <option value="openrouter_gemini3">OpenRouter · Gemini 3 Pro</option>
                            </select>
                        </Field>
                        <label className="flex items-center gap-3 cursor-pointer">
                            <input type="checkbox" checked={form.voting_enabled} onChange={(e) => update("voting_enabled", e.target.checked)} data-testid="voting-cb" />
                            <span className="text-sm font-medium">Cho phép cộng đồng bình chọn</span>
                        </label>
                        <Field label="Trạng thái">
                            <select className="input" value={form.status} onChange={(e) => update("status", e.target.value)} data-testid="status-select">
                                <option value="draft">Nháp</option>
                                <option value="published">Công bố</option>
                                <option value="active">Đang mở</option>
                                <option value="reviewing">Đang chấm</option>
                                <option value="finalized">Đã kết thúc</option>
                                <option value="archived">Lưu trữ</option>
                            </select>
                        </Field>
                    </Section>

                    {error && <div className="text-[var(--red)] text-sm bg-red-50 border border-red-200 rounded-lg p-3 flex gap-2"><AlertCircle className="w-4 h-4 flex-shrink-0 mt-0.5" /> {error}</div>}
                    {success && <div className="text-emerald-700 text-sm bg-emerald-50 border border-emerald-200 rounded-lg p-3">{success}</div>}

                    <div className="flex justify-end gap-3 pt-3">
                        <Link to="/quan-tri" className="btn-secondary">Hủy</Link>
                        <button onClick={save} disabled={saving} className="btn-gold" data-testid="save-contest-btn">
                            <Save className="w-4 h-4" /> {saving ? "Đang lưu…" : isEdit ? "Lưu thay đổi" : "Tạo cuộc thi"}
                        </button>
                    </div>
                </div>
        </div>
    );
}

function Section({ title, icon, children }) {
    return (
        <div>
            <div className="flex items-center gap-2 text-[var(--navy)] font-heading font-bold mb-3 pb-2 border-b border-[var(--border)]">
                <span className="w-7 h-7 rounded-lg bg-[var(--gold)]/15 flex items-center justify-center text-[var(--gold-deep)]">{icon}</span>
                {title}
            </div>
            <div className="space-y-4">{children}</div>
        </div>
    );
}

function Field({ label, required, children }) {
    return (
        <div>
            <label className="block text-xs uppercase tracking-widest text-slate-500 font-semibold mb-1.5">
                {label} {required && <span className="text-[var(--red)]">*</span>}
            </label>
            {children}
        </div>
    );
}
